import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useAppPerformanceContext } from 'src/contexts/AppPerformanceContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { DATA_REFRESH_RATE_IN_MILLIS_LONG } from 'src/utils/constants/constants';
import { Event, ListingClient } from 'src/WebApiController';

export const useGetCompListings = (
  event?: Event | null,
  selectedListingId?: number,
  currency?: string
) => {
  const { trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackEndpoint } = useAppPerformanceContext();

  const shouldQueryCompListings = useMemo(() => {
    return (
      activeAccountWebClientConfig.activeAccountId != null &&
      event?.viagId != null
    );
  }, [activeAccountWebClientConfig.activeAccountId, event?.viagId]);

  const compListingsQuery = useQuery({
    queryKey: [
      'ListingClient.getCompListingsByEventId',
      activeAccountWebClientConfig,
      event?.viagId,
      selectedListingId,
    ],
    queryFn: async () => {
      const abortController = new AbortController();
      return await trackEndpoint(
        'ListingClient.getCompListingsByEventId',
        new ListingClient(
          activeAccountWebClientConfig
        ).getCompListingsByEventId(
          event!.viagId!,
          selectedListingId,
          currency,
          abortController.signal
        ),
        (e: Error) => {
          abortController.abort(e.message);
        }
      );
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(500 * attemptIndex, 3000),
    enabled: shouldQueryCompListings,
    staleTime: DATA_REFRESH_RATE_IN_MILLIS_LONG,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('ListingClient.getCompListingsByEventId', error, {
          trackErrorData: {
            viagogoEventId: event?.viagId,
          },
        });
      },
    },
  });

  return {
    compListingsQuery,
  };
};
